import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User, LogOut, ChevronDown, Search } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import Logo from './Logo';

const Header = () => {
  const { user, logout, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);

    // Redirect to landing page if not already there
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to search results or perform search
      console.log('Searching for:', searchQuery);
      // TODO: Implement search functionality
    }
  };

  // Determine navigation links based on current page
  const getNavigationLinks = () => {
    const currentPath = location.pathname;

    if (currentPath === '/') {
      // On Home page - show Burning and Verify
      return [
        { to: '/burning', label: 'Burning' },
        { to: '/verification', label: 'Verify' },
      ];
    } else if (currentPath === '/burning') {
      // On Burning page - show Home and Verify
      return [
        { to: '/', label: 'Home' },
        { to: '/verification', label: 'Verify' },
      ];
    } else if (currentPath === '/verification') {
      // On Verify page - show Home and Burning
      return [
        { to: '/', label: 'Home' },
        { to: '/burning', label: 'Burning' },
      ];
    }

    // Default fallback
    return [
      { to: '/burning', label: 'Burning' },
      { to: '/verification', label: 'Verify' },
    ];
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Hide header on pages that have the three-column layout (sidebar navigation)
  const pagesWithSidebar = ['/', '/profile', '/verification', '/notifications'];
  const isPostDetailPage = location.pathname.startsWith('/post/');
  const shouldHideHeader =
    user && (pagesWithSidebar.includes(location.pathname) || isPostDetailPage);

  // Hide header completely when on pages with sidebar navigation
  if (shouldHideHeader) {
    return null;
  }

  return (
    <header className={`bg-white shadow-sm ${user ? 'border-b' : ''}`}>
      <div className='px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
        <div className='flex justify-between items-center h-16'>
          <Logo linkTo={user ? '/' : '/'} />
          <nav className='hidden space-x-8 md:flex'>
            {user &&
              getNavigationLinks().map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className='px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700'
                >
                  {link.label}
                </Link>
              ))}
          </nav>
          <div className='flex items-center space-x-4'>
            {user && (
              <form onSubmit={handleSearch} className='hidden md:block'>
                <div className='relative'>
                  <div className='flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none'>
                    <Search className='w-4 h-4 text-gray-400' />
                  </div>
                  <input
                    type='text'
                    placeholder='Search...'
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className='py-2 pr-4 pl-10 w-64 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent'
                  />
                </div>
              </form>
            )}
            {isLoading ? (
              <div className='w-6 h-6 rounded-full border-b-2 border-green-600 animate-spin'></div>
            ) : user ? (
              <div className='relative' ref={dropdownRef}>
                <button
                  onClick={toggleDropdown}
                  className='flex items-center px-3 py-2 space-x-2 text-sm font-medium text-gray-700 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100'
                >
                  <User className='w-4 h-4' />
                  <span>{user.username}</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isDropdownOpen && (
                  <div className='absolute right-0 z-50 py-1 mt-2 w-48 bg-white rounded-lg border border-gray-200 shadow-lg'>
                    <Link
                      to='/profile'
                      className='flex items-center px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100'
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <User className='mr-3 w-4 h-4' />
                      Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className='flex items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors hover:bg-gray-100'
                    >
                      <LogOut className='mr-3 w-4 h-4' />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to='/login'
                  className='px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700'
                >
                  Login
                </Link>
                <Link
                  to='/register'
                  className='px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700'
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
